'use client';

import React, { useState, useEffect } from 'react';

import { motion } from 'framer-motion';
import dynamic from 'next/dynamic';

import { Place } from '@/types';

import BestOfFAQs from './BestOfFAQs';
import BestOfFilters from './BestOfFilters';
import BestOfHeader from './BestOfHeader';
import BestOfPlaceCard from './BestOfPlaceCard';

const MapWithNoSSR = dynamic(() => import('@/components/InteractiveMap'), {
  ssr: false,
});

interface CategoryStats {
  avgRating?: number;
  avgReviews?: number;
  avgPhotos?: number;
  count?: number;
}

interface ClientCategoryPageProps {
  category: string;
  places: Place[];
  stats: CategoryStats;
}

export default function ClientBestofCategoryPage({
  category,
  places = [],
  stats: _stats = {},
}: ClientCategoryPageProps) {
  const [sortBy, setSortBy] = useState('rank');
  const [searchTerm, setSearchTerm] = useState('');
  const [displayedPlaces, setDisplayedPlaces] = useState<Place[]>(places);
  const [selectedSubtype, setSelectedSubtype] = useState('All');

  useEffect(() => {
    const filteredPlaces = places.filter((place: Place) => {
      const nameMatch = place.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
      const subtypeMatch =
        selectedSubtype === 'All' ||
        (Array.isArray(place.subtypes) &&
          place.subtypes.some(subtype =>
            subtype.toLowerCase().includes(selectedSubtype.toLowerCase())
          ));
      return nameMatch && subtypeMatch;
    });

    const sortedPlaces = [...filteredPlaces].sort((a: Place, b: Place) => {
      if (sortBy === 'rank') return (Number(a.rank) || 0) - (Number(b.rank) || 0);
      if (sortBy === 'rating') return (Number(b.rating) || 0) - (Number(a.rating) || 0);
      return (Number(b.reviews) || 0) - (Number(a.reviews) || 0);
    });

    setDisplayedPlaces(sortedPlaces);
  }, [places, sortBy, searchTerm, selectedSubtype]);

  const capitalizedCategory = category.charAt(0).toUpperCase() + category.slice(1);
  const title = `Top ${places.length || 0} ${capitalizedCategory} in the World - Updated Daily by Destinations AI`;

  // Handle case when there are no places
  const topPlaces = displayedPlaces.slice(0, 3).map((place: Place) => place.name);
  const subtitle = places.length
    ? `Explore these top ${category.toLowerCase()} in the world ranked based on our proprietary algorithm to help you plan your trip, such as ${topPlaces.join(', ')}, and more.`
    : `We're currently collecting data on the top ${category.toLowerCase()} in the world. Check back soon for our comprehensive rankings.`;

  const subtypes = [
    'All',
    ...new Set(
      places.flatMap(place => {
        const processSubtype = (subtype: string) =>
          subtype.split(',').map(s =>
            s
              .trim()
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ')
          );

        if (Array.isArray(place.subtypes)) {
          return place.subtypes.flatMap(processSubtype);
        } else if (typeof place.subtypes === 'string') {
          return processSubtype(place.subtypes);
        } else if (place.subtypes) {
          return processSubtype(String(place.subtypes));
        }
        return [];
      })
    ),
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
      <BestOfHeader title={title} subtitle={subtitle} />

      <motion.div
        className="bg-blue-100 dark:bg-blue-900 p-6 rounded-lg shadow-md mb-8"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <h2 className="text-2xl font-semibold mb-4">About Our Ranking</h2>
        <p className="mb-4">
          Our AI-powered algorithm analyzes millions of data points to bring you the most up-to-date
          and accurate rankings of {capitalizedCategory} in the world.
        </p>
        <p className="font-semibold">Key components of our algorithm include:</p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Multi-dimensional quality assessment</li>
          <li>Temporal relevance weighting</li>
          <li>Contextual popularity analysis</li>
          <li>Semantic sentiment evaluation</li>
        </ul>
      </motion.div>

      {places.length > 0 && (
        <MapWithNoSSR
          places={displayedPlaces}
          category={category.toLowerCase().replace(/ /g, '-')}
          destination={{ name: category, value: category }}
        />
      )}

      {places.length > 0 ? (
        <>
          <BestOfFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            sortBy={sortBy}
            setSortBy={setSortBy}
            selectedSubtype={selectedSubtype}
            setSelectedSubtype={setSelectedSubtype}
            subtypes={subtypes}
          />

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            {displayedPlaces.map((place: Place, index: number) => (
              <BestOfPlaceCard key={place._id} place={place} index={index} category={category} />
            ))}
          </motion.div>
        </>
      ) : (
        <motion.div
          className="bg-yellow-50 dark:bg-yellow-900 p-6 rounded-lg shadow-md mb-12 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <h3 className="text-xl font-semibold mb-2">Coming Soon!</h3>
          <p>
            We&apos;re currently compiling data for the top {capitalizedCategory.toLowerCase()} in
            the world. Check back soon for our comprehensive rankings and insights.
          </p>
        </motion.div>
      )}

      <BestOfFAQs
        category={category}
        places={places}
        topPlace={
          displayedPlaces[0] ||
          places[0] || {
            name: `Top ${category}`,
            _id: '0',
            destination: 'various global locations',
            famous_for: 'its unique features',
          }
        }
      />
    </div>
  );
}
